import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

export default function Login() {
  const { user, login, isSuperadmin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot-password inline mode
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  if (user) return <Navigate to={isSuperadmin ? "/admin" : "/products"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u?.role === "superadmin" ? "/admin" : "/products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di accesso");
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
      toast.success("Se l'email esiste, riceverai le istruzioni per il reset.");
    } catch (err) {
      // Endpoint always returns 200 to avoid enumeration; surface network errors only.
      toast.error(err.response?.data?.detail || "Errore di rete. Riprova.");
    } finally {
      setForgotLoading(false);
    }
  };

  const goForgot = () => {
    setForgotEmail(email);
    setForgotSent(false);
    setMode("forgot");
  };
  const backToLogin = () => setMode("login");

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:block lg:w-1/2 relative" style={{
        backgroundImage: `url(${process.env.REACT_APP_BACKEND_URL}/api/files/26d14528-e26f-4d89-9eb6-b46a076045be)`,
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/25 via-transparent to-transparent" />
        <Link to="/" className="absolute top-10 left-12 font-serif text-2xl tracking-tight text-[#1A1A1A] hover:opacity-70 transition-opacity" data-testid="login-brand-home">Cosmely</Link>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="text-[10px] tracking-luxury text-[#1A1A1A]/70 uppercase mb-2">Cosmely &middot; Cosmetic Manager</div>
          <h1 className="font-serif text-5xl text-[#1A1A1A] leading-tight">La confezione la<br/>stampi una volta.<br/>Quello che racconta<br/>lo cambi quando vuoi.</h1>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-8 relative">
        <Link to="/" className="absolute top-8 left-8 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#8A8579] hover:text-[#1A1A1A] transition-colors" data-testid="login-back-home">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Torna alla home
        </Link>
        {mode === "login" ? (
          <form onSubmit={submit} className="w-full max-w-sm animate-fade-in-up" data-testid="login-form">
            <div className="font-serif text-3xl mb-1">Accesso amministratore</div>
            <div className="text-sm text-[#707070] mb-10">Gestionale prodotti cosmetici</div>

            <label className="label-tiny">Email</label>
            <input data-testid="login-email" className="input-luxury mb-5" type="email"
                   value={email} onChange={(e)=>setEmail(e.target.value)} required />

            <label className="label-tiny">Password</label>
            <div className="relative mb-8">
              <input data-testid="login-password" className="input-luxury !mb-0 pr-11"
                     type={showPassword ? "text" : "password"}
                     value={password} onChange={(e)=>setPassword(e.target.value)} required />
              <button type="button" data-testid="toggle-password-visibility"
                      onClick={()=>setShowPassword(v=>!v)}
                      aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                      title={showPassword ? "Nascondi password" : "Mostra password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#707070] hover:text-[#1A1A1A] transition-colors">
                {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>

            <button data-testid="login-submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Accesso in corso..." : "Accedi"}
            </button>

            <div className="text-center mt-6">
              <button type="button" data-testid="forgot-password-btn"
                      onClick={goForgot}
                      className="text-xs text-[#707070] underline">
                Password dimenticata?
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitForgot} className="w-full max-w-sm animate-fade-in-up" data-testid="forgot-form">
            <div className="font-serif text-3xl mb-1">Recupera password</div>
            <div className="text-sm text-[#707070] mb-10">
              Inserisci l&apos;indirizzo del tuo account: ti invieremo un link per scegliere una nuova password.
            </div>

            {forgotSent ? (
              <div className="rounded border border-[#E5E5E0] bg-[#F5F2EC] p-5 text-sm text-[#1A1A1A] mb-6"
                   data-testid="forgot-sent-notice">
                Se l&apos;indirizzo <strong>{forgotEmail}</strong> corrisponde a un account attivo,
                riceverai a breve una email con le istruzioni per reimpostare la password.
                Il link è valido per 60 minuti.
              </div>
            ) : (
              <>
                <label className="label-tiny">Email</label>
                <input data-testid="forgot-email" className="input-luxury mb-8" type="email"
                       value={forgotEmail} onChange={(e)=>setForgotEmail(e.target.value)} required
                       autoComplete="email" />

                <button data-testid="forgot-submit" disabled={forgotLoading}
                        className="btn-primary w-full">
                  {forgotLoading ? "Invio in corso..." : "Invia link di reset"}
                </button>
              </>
            )}

            <div className="text-center mt-6">
              <button type="button" data-testid="forgot-back-to-login"
                      onClick={backToLogin}
                      className="text-xs text-[#707070] underline">
                Torna al login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
